/**
 * Floating `!` / `?` quest markers over NPCs and their buildings.
 *
 * Janeks: *"Add a floating Exclamation and question mark for buildings with
 * quests"* and *"!/? marks on NPCs/Buildings"*.
 *
 * ─── Three states, because two would lie ────────────────────────────────────
 *   `!` gold   a step this NPC can give you right now   (board.offerable)
 *   `?` gold   a step of theirs you can finish now      (active + claimable)
 *   `?` grey   a step of theirs still in progress       (active, not claimable)
 * The grey `?` is the one that is easy to leave out and the one that makes the
 * gold `?` mean something: without it, "no marker" would cover both "nothing
 * here" and "come back later", and the player would re-walk the settlement
 * checking.
 *
 * ─── Anchoring ──────────────────────────────────────────────────────────────
 * A marker follows the NPC's LIVE position from the sim snapshot, not the
 * authored anchor in the map. Routine NPCs walk (`sim/base-sim.ts`'s
 * `updateRoutines`), and a marker pinned to the spawn point would hang over
 * empty ground while its owner rakes a field forty metres away — the same bug
 * that had to be fixed for the talk trigger. A hidden actor (`RoutineStep.
 * hidden`, e.g. asleep indoors) has its marker hidden too: the body is not
 * drawn, so a glyph floating over the roof would be pointing at nobody.
 *
 * ─── Glyphs are generated, not shipped ──────────────────────────────────────
 * The texture is an inline SVG data URI, built here. That keeps the two glyphs
 * out of the asset pipeline, makes them crisp at any camera distance, and —
 * the reason that actually matters — makes them testable: `markerGlyphDataUrl`
 * is a pure string function, so a test can assert the gold/grey split without
 * a GPU. The SVG uses a generic bold sans rather than the Codex display face:
 * a data-URI SVG rendered into a texture cannot pull a webfont.
 */
import * as THREE from 'three';
import type { ActorPose, NpcId, WorldData, WorldObject } from '../types';
import type { StoryBoard } from '../../state/quest-state.types';

export type QuestMarkerKind = 'available' | 'ready' | 'active';

/**
 * Where in the settlement a quest's job physically is, for quests whose target
 * is a PLACE rather than a person. Keyed by quest slug; the value is a placed
 * world object.
 *
 * Empty on purpose. A pin is only right where the quest is actually DONE, and
 * every quest today is completed at an NPC: "The Harvest Will Not Wait" pinned
 * Ashford Farm while the wrench is handed to Bram, so the pin sent players to a
 * farm where nothing happens. Janeks, 2026-10-16: *"remove the marker, state
 * that need to bring wrench to NPC."* Add a row only for a quest the game
 * completes at that place.
 */
export const QUEST_WORLD_TARGETS: Readonly<Record<string, { objectId: string; label: string }>> = {};

export interface QuestTarget {
  slug: string;
  label: string;
  objectId: string;
  position: { x: number; z: number };
  height: number;
}

/** Targets for the quests the player holds and has not finished. Pure; unplaceable ids are skipped. */
export function questTargets(world: WorldData, board: StoryBoard | null): QuestTarget[] {
  if (!board) return [];
  const out: QuestTarget[] = [];
  for (const q of board.active) {
    if (q.claimable) continue;
    const t = QUEST_WORLD_TARGETS[q.slug];
    const obj = t && world.objects.find((o) => o.id === t.objectId);
    if (!t || !obj) continue;
    out.push({
      slug: q.slug,
      label: t.label,
      objectId: obj.id,
      position: { x: obj.position.x, z: obj.position.z },
      height: (obj.positionY ?? 0) + (obj.modelHeight ?? obj.size.h ?? DEFAULT_ANCHOR_HEIGHT),
    });
  }
  return out;
}

/** A map-pin glyph: the "go here" marker, distinct from the NPC `!` / `?`. */
export function pinGlyphDataUrl(): string {
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="96" viewBox="0 0 64 96">'
    + '<path d="M32 90 C32 90 8 52 8 34 a24 24 0 0 1 48 0 C56 52 32 90 32 90Z" stroke="#15120c" stroke-width="6"'
    + ' paint-order="stroke" fill="#d9532e"/><circle cx="32" cy="34" r="10" fill="#15120c"/></svg>';
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Gold reads as "there is something here for you"; grey as "not yet". */
const MARKER_COLORS: Record<QuestMarkerKind, string> = {
  available: '#e8b84b',
  ready: '#e8b84b',
  active: '#9a9183',
};
const MARKER_GLYPHS: Record<QuestMarkerKind, string> = {
  available: '!',
  ready: '?',
  active: '?',
};
/**
 * Highest first. One NPC can hold several steps at once — Bram gives seven —
 * so a single winner has to be picked, and a new job outranks an errand
 * already under way.
 */
const MARKER_PRIORITY: QuestMarkerKind[] = ['available', 'ready', 'active'];

/**
 * Metres above the anchor's own height the glyph floats.
 *
 * Raised from 0.55 when floating name labels landed (`render/name-tags.ts`):
 * the name card sits on the head, spanning `[h+0.03, h+0.65]`, and the marker
 * has to clear it. Name-under-icon is the conventional order and the only one
 * that survives an NPC having both — Bram holds seven quest steps and is also
 * called Bram.
 */
const MARKER_CLEARANCE = 1.05;
/** Metres the glyph bobs, peak to peak. */
const BOB_AMPLITUDE = 0.09;
const BOB_HZ = 0.55;
/** World-space height of the glyph card. The `!` is tall and narrow. */
const MARKER_HEIGHT = 0.62;
const MARKER_ASPECT = 2 / 3;
/** Fallback anchor height when an NPC has no placed body or building. */
const DEFAULT_ANCHOR_HEIGHT = 1.8;

/**
 * Which marker belongs over each NPC key, given a board. Pure.
 *
 * Keys are BACKEND npc keys (`giverNpcKey`/`turnInNpcKey`), which are the same
 * strings as `WorldObject.npcId` for every settlement NPC — resolving them to
 * a position is `resolveAnchors`'s job, and the two are kept separate so an
 * unresolvable key surfaces as data rather than as a marker that silently
 * never renders.
 */
export function markerKindsByNpcKey(board: StoryBoard | null): Map<string, QuestMarkerKind> {
  const out = new Map<string, QuestMarkerKind>();
  if (!board) return out;

  const claim = (key: string, kind: QuestMarkerKind): void => {
    const current = out.get(key);
    if (current && MARKER_PRIORITY.indexOf(current) <= MARKER_PRIORITY.indexOf(kind)) return;
    out.set(key, kind);
  };

  for (const quest of board.active) {
    claim(quest.turnInNpcKey, quest.claimable ? 'ready' : 'active');
  }
  // Offers last: `claim` keeps the higher-priority kind, and 'available' wins
  // over both, so an NPC who can both give and receive shows the `!`.
  for (const offer of board.offerable) {
    claim(offer.giverNpcKey, 'available');
  }
  return out;
}

export interface MarkerAnchor {
  npcKey: string;
  /** The world object the marker hangs over — its live pose is looked up by this id. */
  objectId: string;
  position: { x: number; z: number };
  /** Top of the anchor object, before clearance. */
  height: number;
}

/**
 * Maps NPC keys to something in the world to hang a marker on.
 *
 * Prefers the NPC's own placed body (`kind: 'npc'`) over their building, so
 * the marker tracks the person rather than the roof they work under. Falls
 * back to any interactable object claiming that `npcId` — which is what puts a
 * marker over the Medbay for Iris when only the building is placed, and is
 * exactly the "buildings with quests" half of the request.
 *
 * Returns the keys it could NOT place, rather than dropping them silently: an
 * unresolvable giver means a quest whose marker never appears anywhere and
 * throws nothing, which is the failure this function is shaped to expose.
 */
export function resolveAnchors(
  world: WorldData,
  npcKeys: Iterable<string>,
): { anchors: Map<string, MarkerAnchor>; unresolved: string[] } {
  const bodies = new Map<string, WorldObject>();
  const buildings = new Map<string, WorldObject>();
  for (const obj of world.objects) {
    if (!obj.npcId) continue;
    if (obj.kind === 'npc') {
      if (!bodies.has(obj.npcId)) bodies.set(obj.npcId, obj);
    } else if (obj.interactable && !buildings.has(obj.npcId)) {
      buildings.set(obj.npcId, obj);
    }
  }

  const anchors = new Map<string, MarkerAnchor>();
  const unresolved: string[] = [];
  for (const key of npcKeys) {
    const obj = bodies.get(key) ?? buildings.get(key);
    if (!obj) {
      unresolved.push(key);
      continue;
    }
    anchors.set(key, {
      npcKey: key,
      objectId: obj.id,
      position: { x: obj.position.x, z: obj.position.z },
      height: (obj.positionY ?? 0) + (obj.modelHeight ?? obj.size.h ?? DEFAULT_ANCHOR_HEIGHT),
    });
  }
  return { anchors, unresolved };
}

/**
 * The glyph as an SVG data URI.
 *
 * `paint-order="stroke"` draws the dark outline BEHIND the fill rather than
 * over it, so the glyph keeps its full weight against a bright sky and still
 * reads against a pale wall. Without it the stroke eats half the letter.
 */
export function markerGlyphDataUrl(kind: QuestMarkerKind): string {
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="96" viewBox="0 0 64 96">'
    + '<text x="32" y="78" text-anchor="middle" font-size="88" font-weight="700"'
    + ' font-family="Impact, \'Arial Black\', Helvetica, sans-serif"'
    + ` stroke="#15120c" stroke-width="8" paint-order="stroke" fill="${MARKER_COLORS[kind]}">`
    + `${MARKER_GLYPHS[kind]}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export interface QuestMarkers {
  /** Add this to the scene once; everything below mutates its children. */
  readonly object: THREE.Group;
  /**
   * Rebuilds the marker set from a board. Cheap to call on every store push —
   * it no-ops when the resulting key→kind map is unchanged.
   */
  setBoard(board: StoryBoard | null): void;
  /** The place-markers currently up (the farm pump, ...). */
  readonly targets: readonly QuestTarget[];
  /** Per-frame: follow live actor poses and bob. */
  update(elapsed: number, actors: readonly ActorPose[]): void;
  /** NPC keys the last `setBoard` could not place — for diagnostics and tests. */
  readonly unresolved: readonly string[];
  dispose(): void;
}

interface LiveMarker {
  sprite: THREE.Sprite;
  anchor: MarkerAnchor;
  /** Phase offset so a row of markers does not bob in lockstep. */
  phase: number;
}

export function createQuestMarkers(world: WorldData): QuestMarkers {
  const group = new THREE.Group();
  group.name = 'quest-markers';
  const loader = new THREE.TextureLoader();
  /** One texture per kind, shared by every marker showing it. */
  const textures = new Map<QuestMarkerKind, THREE.Texture>();
  let markers: LiveMarker[] = [];
  let unresolved: string[] = [];
  let signature = '';
  let targets: QuestTarget[] = [];
  let targetSprites: THREE.Sprite[] = [];
  let targetTex: THREE.Texture | null = null;

  const textureFor = (kind: QuestMarkerKind): THREE.Texture => {
    let tex = textures.get(kind);
    if (!tex) {
      tex = loader.load(markerGlyphDataUrl(kind));
      tex.colorSpace = THREE.SRGBColorSpace;
      textures.set(kind, tex);
    }
    return tex;
  };

  const clear = (): void => {
    for (const m of markers) {
      group.remove(m.sprite);
      m.sprite.material.dispose();
    }
    markers = [];
  };

  const setTargets = (board: StoryBoard | null): void => {
    const next = questTargets(world, board);
    if (next.map((t) => t.slug).join(',') === targets.map((t) => t.slug).join(',')) return;
    for (const sp of targetSprites) { group.remove(sp); sp.material.dispose(); }
    targetSprites = [];
    targets = next;
    if (next.length && !targetTex) {
      targetTex = loader.load(pinGlyphDataUrl());
      targetTex.colorSpace = THREE.SRGBColorSpace;
    }
    for (const t of next) {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({
        map: targetTex, transparent: true, depthTest: false, depthWrite: false, fog: false,
      }));
      sp.scale.set(MARKER_HEIGHT * 1.15 * MARKER_ASPECT, MARKER_HEIGHT * 1.15, 1);
      sp.position.set(t.position.x, t.height + MARKER_CLEARANCE, t.position.z);
      sp.renderOrder = 20;
      sp.name = `quest-target:${t.slug}`;
      group.add(sp);
      targetSprites.push(sp);
    }
  };

  const setBoard = (board: StoryBoard | null): void => {
    setTargets(board);
    const kinds = markerKindsByNpcKey(board);
    // Sorted so the signature is order-independent — the board's array order
    // is the server's, and a reshuffle must not rebuild every sprite.
    const next = [...kinds.entries()].sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${v}`).join(',');
    if (next === signature) return;
    signature = next;

    clear();
    const resolved = resolveAnchors(world, kinds.keys());
    unresolved = resolved.unresolved;
    let i = 0;
    for (const [key, kind] of kinds) {
      const anchor = resolved.anchors.get(key);
      if (!anchor) continue;
      const material = new THREE.SpriteMaterial({
        map: textureFor(kind),
        transparent: true,
        // Markers are wayfinding, not scenery — they must stay legible through
        // a wall or a tree rather than being occluded by them.
        depthTest: false,
        depthWrite: false,
        // Deliberately NOT fog-affected: this settlement has fog zones, and a
        // marker fading out is indistinguishable from no marker at all.
        fog: false,
      });
      const sprite = new THREE.Sprite(material);
      sprite.scale.set(MARKER_HEIGHT * MARKER_ASPECT, MARKER_HEIGHT, 1);
      sprite.position.set(anchor.position.x, anchor.height + MARKER_CLEARANCE, anchor.position.z);
      // Above everything else in the scene, for the same reason as depthTest.
      sprite.renderOrder = 20;
      sprite.name = `quest-marker:${key}`;
      sprite.userData.npcKey = key;
      sprite.userData.kind = kind;
      group.add(sprite);
      markers.push({ sprite, anchor, phase: (i++ * Math.PI) / 3 });
    }
  };

  const update = (elapsed: number, actors: readonly ActorPose[]): void => {
    // The pin bobs a little harder than an NPC glyph: it is the thing to follow.
    targetSprites.forEach((sp, i) => {
      const t = targets[i]!;
      sp.position.y = t.height + MARKER_CLEARANCE + Math.sin(elapsed * 1.1 * Math.PI * 2) * 0.12;
    });
    if (markers.length === 0) return;
    // Built per call rather than cached: `actors` is a fresh array from the
    // sim each frame, and the marker set is small enough (single digits) that
    // the map costs less than tracking invalidation would.
    const live = new Map<string, ActorPose>();
    for (const a of actors) live.set(a.objectId, a);

    for (const m of markers) {
      const pose = live.get(m.anchor.objectId);
      if (pose?.hidden) {
        m.sprite.visible = false;
        continue;
      }
      m.sprite.visible = true;
      const x = pose ? pose.position.x : m.anchor.position.x;
      const z = pose ? pose.position.z : m.anchor.position.z;
      const bob = Math.sin(elapsed * BOB_HZ * Math.PI * 2 + m.phase) * (BOB_AMPLITUDE / 2);
      m.sprite.position.set(x, m.anchor.height + MARKER_CLEARANCE + bob, z);
    }
  };

  return {
    object: group,
    setBoard,
    update,
    get targets() { return targets; },
    get unresolved() { return unresolved; },
    dispose() {
      clear();
      for (const sp of targetSprites) { group.remove(sp); sp.material.dispose(); }
      targetSprites = [];
      targetTex?.dispose();
      for (const tex of textures.values()) tex.dispose();
      textures.clear();
    },
  };
}

/** Re-exported for tests that want to assert on the world's own npc ids. */
export type { NpcId };
